import { type FormEvent, useState } from 'react';
import { Button, Card, Field, Icon, Select, TextInput, Textarea } from '../components/ui/index.js';
import { useAdminLocale } from '../lib/admin-locale.js';
import { readJson } from '../lib/remote.js';

const STYLES = [
  {
    id: 'friendly',
    line: 'Ύφος: Φιλικό και επαγγελματικό.',
    label: ['Φιλικό και επαγγελματικό', 'Friendly and professional'],
  },
  {
    id: 'formal',
    line: 'Ύφος: Επίσημο και ευγενικό.',
    label: ['Επίσημο και ευγενικό', 'Formal and polite'],
  },
  {
    id: 'direct',
    line: 'Ύφος: Ουδέτερο και άμεσο.',
    label: ['Ουδέτερο και άμεσο', 'Neutral and direct'],
  },
] as const;

const LENGTHS = [
  {
    id: 'short',
    line: 'Έκταση: Σύντομες απαντήσεις, μία ή δύο προτάσεις.',
    label: ['Σύντομες απαντήσεις, μία ή δύο προτάσεις', 'Short answers, one or two sentences'],
  },
  {
    id: 'medium',
    line: 'Έκταση: Μέτριες απαντήσεις, με τα απαραίτητα στοιχεία.',
    label: [
      'Μέτριες απαντήσεις, με τα απαραίτητα στοιχεία',
      'Medium-length answers with the necessary details',
    ],
  },
  {
    id: 'long',
    line: 'Έκταση: Πιο αναλυτικές απαντήσεις, όταν το επιτρέπουν οι πηγές.',
    label: [
      'Πιο αναλυτικές απαντήσεις, όταν το επιτρέπουν οι πηγές',
      'More detailed answers when supported by the sources',
    ],
  },
] as const;

const LOCALE_LABEL: Record<string, readonly [string, string]> = {
  el: ['Ελληνικά', 'Greek'],
  en: ['English', 'English'],
  fr: ['Français', 'French'],
};

interface LocaleRow {
  locale: string;
  greeting: string;
  fallback: string;
  enabled: boolean;
}

interface BotInstructions {
  name: string;
  defaultLocale: string;
  locales: LocaleRow[];
  settings: { businessInstructions?: string | null };
}

function readGuide(value: string | null | undefined): {
  style: (typeof STYLES)[number]['id'];
  length: (typeof LENGTHS)[number]['id'];
  notes: string;
} {
  const text = value ?? '';
  const lines = text.split('\n');
  const style = STYLES.find((item) => item.line === lines[0]);
  const length = LENGTHS.find((item) => item.line === lines[1]);
  if (style && length) {
    return { style: style.id, length: length.id, notes: lines.slice(2).join('\n').trim() };
  }
  return { style: 'friendly', length: 'short', notes: text.trim() };
}

function composeGuide(style: string, length: string, notes: string): string {
  const styleLine = STYLES.find((item) => item.id === style)?.line ?? STYLES[0].line;
  const lengthLine = LENGTHS.find((item) => item.id === length)?.line ?? LENGTHS[0].line;
  const extra = notes.trim();
  return extra ? `${styleLine}\n${lengthLine}\n${extra}` : `${styleLine}\n${lengthLine}`;
}

export function InstructionsPanel(props: {
  siteId: string;
  bot: BotInstructions;
  canWrite: boolean;
  onSaved: () => void;
}) {
  const { text } = useAdminLocale();
  const initial = readGuide(props.bot.settings.businessInstructions);
  const [name, setName] = useState(props.bot.name);
  const [style, setStyle] = useState(initial.style);
  const [length, setLength] = useState(initial.length);
  const [notes, setNotes] = useState(initial.notes);
  const [locale, setLocale] = useState(props.bot.defaultLocale);
  const [fallbacks, setFallbacks] = useState<Record<string, string>>(() =>
    Object.fromEntries(props.bot.locales.map((item) => [item.locale, item.fallback])),
  );
  const [pending, setPending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const current = props.bot.locales.find((item) => item.locale === locale) ?? props.bot.locales[0];
  const fallback = current ? (fallbacks[current.locale] ?? '') : '';

  async function save(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!current) return;
    setPending(true);
    setNotice(null);
    setError(null);
    try {
      await readJson(`/v1/sites/${props.siteId}/bot`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: name.trim(),
          locales: props.bot.locales.map((item) => ({
            locale: item.locale,
            enabled: item.enabled,
            greeting: item.greeting,
            fallback: (fallbacks[item.locale] ?? item.fallback).trim(),
          })),
          settings: { businessInstructions: composeGuide(style, length, notes) },
        }),
      });
      setNotice(text('Αποθηκεύτηκε.', 'Saved.'));
      props.onSaved();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : text('Η αποθήκευση απέτυχε', 'Save failed'),
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <form
      onSubmit={(event) => void save(event)}
      className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]"
    >
      <div className="space-y-4">
        <Card
          title={text('Ταυτότητα βοηθού', 'Assistant identity')}
          description={text(
            'Το όνομα φαίνεται στο chatbot. Το ύφος και η έκταση δίνονται στο μοντέλο και δεν αντικαθιστούν τους κανόνες των πηγών.',
            'The name appears in the chatbot. Tone and response length are passed to the model and do not replace source rules.',
          )}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={text('Όνομα βοηθού', 'Assistant name')}>
              <TextInput
                required
                disabled={!props.canWrite}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </Field>
            <Field label={text('Ύφος επικοινωνίας', 'Communication tone')}>
              <Select
                className="mt-1.5"
                value={style}
                disabled={!props.canWrite}
                onChange={(next) => setStyle(next as typeof style)}
                options={STYLES.map((item) => ({
                  value: item.id,
                  label: text(item.label[0], item.label[1]),
                }))}
              />
            </Field>
            <Field label={text('Έκταση απαντήσεων', 'Response length')}>
              <Select
                className="mt-1.5"
                value={length}
                disabled={!props.canWrite}
                onChange={(next) => setLength(next as typeof length)}
                options={LENGTHS.map((item) => ({
                  value: item.id,
                  label: text(item.label[0], item.label[1]),
                }))}
              />
            </Field>
          </div>
          <div className="mt-4">
            <Field
              label={text('Επιπλέον οδηγίες', 'Additional instructions')}
              hint={text(
                'Προαιρετικά. Δεν επιτρέπεται να ζητούν απάντηση έξω από τις πηγές.',
                'Optional. They must not request answers outside the sources.',
              )}
            >
              <Textarea
                value={notes}
                disabled={!props.canWrite}
                maxLength={3800}
                rows={4}
                onChange={(event) => setNotes(event.target.value)}
              />
            </Field>
          </div>
        </Card>
        <Card
          title={text('Όταν δεν υπάρχει απάντηση', 'When there is no answer')}
          description={text(
            'Ο βοηθός δεν επινοεί πληροφορία. Δείχνει αυτό το μήνυμα και ο επισκέπτης μπορεί να αφήσει επαφή από τη φόρμα του chatbot.',
            'The assistant does not invent information. It shows this message, and the visitor can leave contact details through the chatbot form.',
          )}
        >
          <Field label={text('Γλώσσα μηνύματος', 'Message language')}>
            <Select
              className="mt-1.5"
              value={current?.locale ?? locale}
              onChange={setLocale}
              options={props.bot.locales.map((item) => ({
                value: item.locale,
                label: LOCALE_LABEL[item.locale]
                  ? text(...LOCALE_LABEL[item.locale]!)
                  : item.locale,
              }))}
            />
          </Field>
          <div className="mt-4">
            <Field
              label={text('Μήνυμα', 'Message')}
              hint={text(
                'Μέχρι 500 χαρακτήρες. Η γλώσσα της απάντησης ακολουθεί τον επισκέπτη.',
                'Up to 500 characters. The response language follows the visitor.',
              )}
            >
              <Textarea
                required
                disabled={!props.canWrite}
                maxLength={500}
                rows={3}
                value={fallback}
                onChange={(event) =>
                  setFallbacks((currentFallbacks) => ({
                    ...currentFallbacks,
                    [current?.locale ?? locale]: event.target.value,
                  }))
                }
              />
            </Field>
          </div>
          {props.canWrite ? (
            <div className="mt-4 flex items-center gap-3">
              <Button type="submit" disabled={pending}>
                <Icon name="save" className="size-4" />
                {pending ? text('Αποθήκευση…', 'Saving…') : text('Αποθήκευση', 'Save')}
              </Button>
              {notice ? (
                <p className="text-sm text-emerald-700 dark:text-emerald-300">{notice}</p>
              ) : null}
              {error ? <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}
            </div>
          ) : null}
        </Card>
      </div>
      <Card
        title={text(
          'Παράδειγμα όταν δεν υπάρχει πληροφορία',
          'Example when information is unavailable',
        )}
        description={text(
          'Δεν είναι πραγματική συνομιλία. Δείχνει το μήνυμα που θα δει ο επισκέπτης.',
          'This is not a real conversation. It shows the message the visitor will see.',
        )}
      >
        <div className="space-y-3 rounded-xl bg-slate-50 dark:bg-slate-950 p-4 text-sm">
          <p className="ml-8 rounded-lg bg-blue-600 px-3 py-2 text-white">
            {text(
              'Ερώτηση που δεν καλύπτεται από τις πηγές',
              'A question not covered by the sources',
            )}
          </p>
          <p className="mr-8 rounded-lg bg-white dark:bg-slate-900 px-3 py-2 text-slate-800 dark:text-slate-200 shadow-sm">
            {fallback || '—'}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
            {text('Όνομα βοηθού:', 'Assistant name:')} {name || '—'}
          </p>
        </div>
      </Card>
    </form>
  );
}
